import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'Components/Atoms/Button/Button';
import FormProvider, {
    RHFSelectMultipleTags,
    RHFSelectProduct,
    RHFSelect,
    RHFInput,
    RHFTextarea,
} from 'Components/Organisms/HookForm';
import RHFSelectMultipleTaxes from 'Components/Organisms/HookForm/RHFSelectMultipleTaxes';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Tabs, { Tab } from 'Components/Molecules/Tabs/Tabs';
import useCountries from 'Hooks/useCountries';
import Skeleton from 'Components/Atoms/Skeleton/Skeleton';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from 'Utils/formatters';
import useCurrencies from 'Hooks/useCurrencies';
import InputTags from 'Components/Molecules/InputTags/InputTags';
import useDebounce from 'Hooks/useDebounce';
import useCategories from 'Hooks/useCategories';
import Divider from 'Components/Atoms/Divider/Divider';
import useDrawerStack from 'Hooks/useDrawerStack';
import { CATEGORY_COLORS, DRAWER_STACK } from 'Utils/constants';
import { createCategory } from 'Services/categories';
import { useQueryClient } from '@tanstack/react-query';
import useWorkspaceSelected from 'Hooks/useWorkspaceSelected';
import useIntegrations from 'Hooks/useIntegrations';
import ContactEditorDrawerDefaultValuesForm from './ContactEditorDrawerDefaultValuesForm';
import useContactTaxAuthorityConfig from 'Hooks/useContactTaxAuthorityConfig';
import {
    createContact,
    updateContact,
    createInvoiceTaxAuthorityConfig,
    updateInvoiceTaxAuthorityConfig,
    deleteInvoiceTaxAuthorityConfig,
} from 'Services/invoices';
import useHasEnabledTaxAuthority from 'Hooks/useHasEnabledTaxAuthority';

const contactTypes = [
    {
        id: 'company',
        i18nKey: 'company',
    },
    {
        id: 'freelancer',
        i18nKey: 'freelancer',
    },
    {
        id: 'individual',
        i18nKey: 'individual',
    },
];

const contactSubtypes = [
    { id: 'client', i18nKey: 'client' },
    { id: 'supplier', i18nKey: 'supplier' },
    { id: 'employee', i18nKey: 'employee' },
    { id: 'investor', i18nKey: 'investor' },
    { id: 'partner', i18nKey: 'partner' },
    { id: 'public_entity', i18nKey: 'public-entity' },
    { id: 'financial_institution', i18nKey: 'financial-institution' },
];

const taxIdTypes = [
    { id: 'national_identity_document', i18nKey: 'national-identity-document' },
    { id: 'nif_vat_identity', i18nKey: 'nif-vat-identity' },
    { id: 'passport', i18nKey: 'passport' },
    { id: 'foreign_identity_document', i18nKey: 'foreign-identity-document' },
    { id: 'residential_certificate', i18nKey: 'residential-certificate' },
    { id: 'other_identity_document', i18nKey: 'other-identity-document' },
    { id: 'not_registered_in_census', i18nKey: 'not-registered-in-census' },
];

const languages = [
    {
        id: '',
        i18nKey: 'none',
    },
    {
        id: 'ES',
        i18nKey: 'spanish',
    },
    {
        id: 'EN',
        i18nKey: 'english',
    },
];

const dueDays = [
    {
        id: '',
        i18nKey: 'none',
    },
    {
        id: 0,
        i18nKey: 'immediate-reception',
    },
    {
        id: 7,
        i18nKey: '7-days',
    },
    {
        id: 15,
        i18nKey: '15-days',
    },
    {
        id: 30,
        i18nKey: '30-days',
    },
    {
        id: 60,
        i18nKey: '60-days',
    },
    {
        id: 90,
        i18nKey: '90-days',
    },
];

export const TABS = {
    GENERAL: 'GENERAL',
    BILLING_DATA: 'BILLING_DATA',
    DEFAULT_VALUES: 'DEFAULT_VALUES',
};

const normalizeBody = (body) => {
    let result = {};

    Object.entries(body).forEach(([key, value]) => {
        if (typeof value === 'string') {
            const normalizedValue = value.trim() === '' ? null : value;
            result[key] = normalizedValue;
        } else {
            result[key] = value;
        }
    });

    return result;
};

const useNormalizeCategories = () => {
    const { categories: categoriesList } = useCategories();

    return function normalizeCategories(ids) {
        const categories = categoriesList.filter((category) => ids.includes(category.id));
        const categoriesWithParentIds = categories.flatMap(({ id, parent_tag_id }) =>
            [id, parent_tag_id].filter(Boolean),
        );
        const uniqueIds = [...new Set(categoriesWithParentIds)];

        return uniqueIds;
    };
};

export default function ContactEditorDrawer(props) {
    const {
        onSubmit,
        onCancel,
        onError,
        contact,
        hasAutomaticKeyword = false,
        initialTab = TABS.GENERAL,
        highlightFields = [],
        submitButtonText,
        cancelButtonText,
    } = props;

    const [keywords, setKeywords] = useState(contact.categorization_keywords ?? []);
    const previousName = useRef('');

    const queryClient = useQueryClient();
    const [processing, setProcessing] = useState(false);
    const { t } = useTranslation('contacts');
    const normalizeCategories = useNormalizeCategories();
    const { workspace } = useWorkspaceSelected();
    const { integrations } = useIntegrations();
    const { hasEnabledTaxAuthority, taxAuthority } = useHasEnabledTaxAuthority();

    const showVerifactuConfig = hasEnabledTaxAuthority;

    const { data: existingConfig, isLoading: isLoadingTaxAuthority } = useContactTaxAuthorityConfig(contact.id);
    const [defaultValuesEnabled, setDefaultValuesEnabled] = useState(false);
    const [taxAuthorityValues, setTaxAuthorityValues] = useState({
        contact_id: contact.id,
        es_verifactu_doc_type: null,
        es_verifactu_simplified_art7273: 'N',
        es_verifactu_issuer_type: null,
        representative_name: null,
        representative_tax_id: null,
        representative_country: null,
        es_verifactu_regime: '01',
    });

    useEffect(() => {
        setDefaultValuesEnabled(!!existingConfig);

        if (taxAuthority === 'ticketbai') {
            setTaxAuthorityValues({
                contact_id: contact.id,
                es_tbai_doc_type: existingConfig?.es_tbai_doc_type ?? null,
                es_tbai_issuer_type: existingConfig?.es_tbai_issuer_type ?? null,
                es_tbai_regime: existingConfig?.es_tbai_regime ?? null,
            });
        } else if (taxAuthority === 'verifactu') {
            setTaxAuthorityValues({
                contact_id: contact.id,
                es_verifactu_doc_type: existingConfig?.es_verifactu_doc_type ?? null,
                es_verifactu_simplified_art7273: existingConfig?.simplified_art7273_bool ? 'Y' : 'N',
                es_verifactu_issuer_type: existingConfig?.es_verifactu_issuer_type ?? null,
                representative_name: existingConfig?.representative_name ?? null,
                representative_tax_id: existingConfig?.representative_tax_id ?? null,
                representative_country: existingConfig?.representative_country ?? null,
                es_verifactu_regime: existingConfig?.es_verifactu_regime ?? '01',
            });
        }
    }, [existingConfig, taxAuthority]);

    const defaultSubmitButtonText = t('save');
    const defaultCancelButtonText = t('cancel');

    const submitButton = submitButtonText || defaultSubmitButtonText;
    const cancelButton = cancelButtonText || defaultCancelButtonText;

    const integration = useMemo(() => {
        if (!contact?.integration_id) return null;

        return integrations.find((int) => int.id === contact.integration_id) ?? null;
    }, [contact?.integration_id, integrations]);

    const canUpdateInIntegration = integration?.capabilities?.includes('UPDATE_CONTACT');
    const disabled = false;

    const contactTypesOptions = contactTypes.map((contactType) => ({
        value: contactType.id,
        label: t(contactType.i18nKey),
    }));

    const contactSubtypesOptions = contactSubtypes.map((subtype) => ({
        value: subtype.id,
        label: t(subtype.i18nKey),
    }));

    const taxIdTypesOptions = taxIdTypes.map((type) => ({
        value: type.id,
        label: t(type.i18nKey),
    }));

    const languagesOptions = languages.map((language) => ({
        value: language.id,
        label: t(language.i18nKey),
    }));

    const paymentDaysOptions = useMemo(() => {
        const none = { value: 'estimated', label: t('estimated') };

        const items = Array.from({ length: 31 }).map((_, index) => ({
            value: index + 1,
            label: `${index + 1}`,
        }));

        return [none, ...items];
    }, []);

    const dueDaysOptions = dueDays.map((day) => ({
        value: day.id,
        label: t(day.i18nKey),
    }));

    const { currencies } = useCurrencies();
    const { countries, isLoading } = useCountries();

    const countriesOptions = countries.map((country) => ({
        value: country.code,
        label: country.name,
    }));

    const currenciesOptions = useMemo(
        () =>
            currencies.map((item) => ({
                value: item.code,
                label: item.name + ' (' + getCurrencySymbol('es-ES', item.code) + ')',
            })),
        [currencies],
    );
    const { open } = useDrawerStack();
    const [toolTab, setToolTab] = useState(initialTab);

    const invoiceSchema = yup.object().shape({
        type: yup.string().required(),
        subtype: yup.string(),
        name: yup.string().required(t('name-is-required')),
        note: yup.string(),
        email: yup.string(),
        tax_id: yup.string(),
        tax_id_type: yup.string().required(),
        phone_number: yup.string(),
        website: yup.string().max(256, t('website-max-length')),
        address: yup.string(),
        postal_code: yup.string(),
        city: yup.string(),
        state: yup.string(),
        country: yup.string(),
    });

    const methods = useForm({
        resolver: yupResolver(invoiceSchema),
        defaultValues: {
            type: contact.type || contactTypes[0].id,
            subtype: contact.subtype || '',
            name: contact.name ?? '',
            note: contact.note ?? '',
            email: contact.email ?? '',
            tax_id: contact.tax_id ?? '',
            tax_id_type: contact.tax_id_type ?? taxIdTypes[0].id,
            phone_number: contact.phone_number ?? '',
            website: contact.website ?? '',
            address: contact.address ?? '',
            postal_code: contact.postal_code ?? '',
            city: contact.city ?? '',
            state: contact.state ?? '',
            country: contact.country || '' || workspace.country || '',
            categories_ids: contact.categories_ids ?? [],
            language: contact.language ?? '',
            currency: contact.currency ?? '',
            due_days: contact.due_days ?? '',
            payment_day: contact.payment_day ?? 'estimated',
            incomes_product_id: contact.incomes_product_id ?? '',
            expenses_product_id: contact.expenses_product_id ?? '',
            taxes_incomes_ids: contact.taxes_incomes_ids ?? [],
            taxes_expenses_ids: contact.taxes_expenses_ids ?? [],
        },
    });

    const { handleSubmit, watch, setValue } = methods;

    const name = watch('name');
    const debouncedName = useDebounce(name, 600);

    useEffect(() => {
        if (!hasAutomaticKeyword) {
            return;
        }

        const normalizedName = debouncedName.trim().toLowerCase();

        if (normalizedName === '') {
            return;
        }

        if (previousName.current !== '') {
            const newKeywords = keywords.map((keyword) =>
                keyword === previousName.current ? normalizedName : keyword,
            );
            setKeywords(newKeywords);
        } else {
            setKeywords([...keywords, normalizedName]);
        }

        previousName.current = normalizedName;
    }, [debouncedName]);

    const submit = async (data) => {
        setProcessing(true);
        if (data.payment_day === 'estimated') {
            data.payment_day = null;
        }

        const normalizedBody = normalizeBody({
            ...data,
            categorization_keywords: keywords,
            categories_ids: normalizeCategories(data.categories_ids),
        });

        try {
            const savedContact = contact.id
                ? await updateContact(contact.id, normalizedBody)
                : await createContact(normalizedBody);

            await queryClient.invalidateQueries({ queryKey: ['contacts'] });

            if (showVerifactuConfig && savedContact) {
                if (defaultValuesEnabled && !existingConfig) {
                    await createInvoiceTaxAuthorityConfig({ ...taxAuthorityValues, contact_id: savedContact.id });
                } else if (defaultValuesEnabled && existingConfig) {
                    await updateInvoiceTaxAuthorityConfig(existingConfig.id, {
                        ...taxAuthorityValues,
                        contact_id: savedContact.id,
                    });
                } else if (!defaultValuesEnabled && existingConfig) {
                    await deleteInvoiceTaxAuthorityConfig(existingConfig.id);
                }

                await queryClient.invalidateQueries({ queryKey: ['invoice-tax-authority-config', savedContact.id] });
            }

            onSubmit(savedContact);
        } catch (error) {
            onError(error, {
                title: contact.id ? t('error-updating-contact-title') : t('error-creating-contact-title'),
                description: contact.id
                    ? t('error-updating-contact-description')
                    : t('error-creating-contact-description'),
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleAddTag = () => {
        const randomColor = Math.floor(Math.random() * CATEGORY_COLORS.length);
        const color = CATEGORY_COLORS[randomColor];

        open(DRAWER_STACK.TAG_EDITOR, {
            title: t('create-tag'),
            tag: {
                name: '',
                color,
                icon: '💰',
            },
            onSubmit: async (data) => {
                const tag = await createCategory(data);
                await queryClient.invalidateQueries({ queryKey: ['categories'] });
                setValue('categories_ids', [...watch('categories_ids'), tag.id]);
            },
        });
    };
    return (
        <FormProvider className="flex flex-col h-full" methods={methods} onSubmit={handleSubmit(submit)}>
            {integration && (
                <div className="shrink-0 bg-[rgba(255,195,123,0.36)] rounded-md p-3 flex gap-3 text-sm text-dark-blue items-center mb-3">
                    <div className="w-6 shrink-0 py-1">
                        <img src={integration.icon} alt="" width={24} height={24} className="w-full h-auto block" />
                    </div>
                    <div>
                        {canUpdateInIntegration
                            ? t('contact-will-update-in-integration', { integrationName: integration.name })
                            : t('contact-sync-will-overwrite', { integrationName: integration.name })}
                    </div>
                </div>
            )}

            <div className="pb-6">
                <Tabs value={toolTab} onChange={setToolTab} className="max-w-full overflow-x-auto">
                    <Tab value={TABS.GENERAL}>{t('general')}</Tab>
                    <Tab value={TABS.BILLING_DATA}>{t('billing-data')}</Tab>
                    <Tab value={TABS.DEFAULT_VALUES}>{t('default-values')}</Tab>
                </Tabs>
            </div>

            <div className="grow pb-6">
                {toolTab === TABS.GENERAL && (
                    <div className="grid gap-4">
                        <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
                            <RHFSelect
                                disabled={disabled}
                                label={t('type')}
                                name="type"
                                options={contactTypesOptions}
                                highlight={highlightFields.includes('type')}
                                className="w-full"
                            />

                            <RHFSelect
                                disabled={disabled}
                                label={t('subtype')}
                                name="subtype"
                                options={contactSubtypesOptions}
                                highlight={highlightFields.includes('subtype')}
                                className="w-full"
                            />
                        </div>

                        <RHFInput
                            disabled={disabled}
                            className="w-full"
                            name="name"
                            data-autofocus="true"
                            highlight={highlightFields.includes('name')}
                            label={t('name')}
                        />

                        <RHFInput
                            disabled={disabled}
                            highlight={highlightFields.includes('website')}
                            name="website"
                            label={t('website')}
                        />

                        <RHFTextarea disabled={disabled} label={t('internal-note')} name="note" />

                        <div className="bg-[#fafafb] border border-gray-200 rounded-xl p-5 mt-4">
                            <div className="flex items-center gap-2 mb-2">
                                <svg
                                    className="w-5 h-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                    />
                                </svg>
                                <span className="font-medium text-dark-blue">{t('assign-tags-title')}</span>
                            </div>
                            <p className="text-sm text-gray-500 mb-4">{t('contact-categories-description')}</p>
                            <RHFSelectMultipleTags
                                name="categories_ids"
                                highlight={highlightFields.includes('categories_ids')}
                                className="w-full"
                                onAdd={handleAddTag}
                            />
                        </div>

                        <div className="bg-[#fff2ee] border border-[#ebe1e1] rounded-xl p-5 mt-2">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-orange-400">✨</span>
                                <span className="font-medium text-dark-blue">{t('automatic-assignment')}</span>
                            </div>
                            <p className="text-sm text-gray-500 mb-4">{t('automatic-assignment-description')}</p>
                            <InputTags
                                tags={keywords}
                                highlight={highlightFields.includes('categorization_keywords')}
                                onChange={setKeywords}
                                placeholder={t('keywords-placeholder')}
                            />
                            <p className="text-sm text-gray-500 mt-3">{t('keywords-description')}</p>
                        </div>
                    </div>
                )}

                {toolTab === TABS.BILLING_DATA && (
                    <div className="grid gap-6">
                        <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
                            <RHFInput
                                disabled={disabled}
                                highlight={highlightFields.includes('tax_id')}
                                name="tax_id"
                                label={t('tax-id')}
                            />

                            <RHFSelect
                                disabled={disabled}
                                label={t('tax-id-type')}
                                name="tax_id_type"
                                options={taxIdTypesOptions}
                                highlight={highlightFields.includes('tax_id_type')}
                                className="w-full"
                            />
                        </div>
                        <Divider className="my-4" />
                        <RHFInput
                            disabled={disabled}
                            highlight={highlightFields.includes('address')}
                            name="address"
                            label={t('address')}
                        />
                        <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
                            <RHFInput
                                disabled={disabled}
                                highlight={highlightFields.includes('postal_code')}
                                name="postal_code"
                                label={t('postal-code')}
                            />
                            <RHFInput
                                disabled={disabled}
                                highlight={highlightFields.includes('city')}
                                name="city"
                                label={t('city')}
                            />
                        </div>
                        <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
                            <RHFInput
                                disabled={disabled}
                                highlight={highlightFields.includes('state')}
                                name="state"
                                label={t('state')}
                            />
                            {isLoading ? (
                                <Skeleton variant="rounded" height={44} />
                            ) : (
                                <RHFSelect
                                    disabled={disabled}
                                    options={countriesOptions}
                                    name="country"
                                    highlight={highlightFields.includes('country')}
                                    label={t('country')}
                                    className="w-full"
                                    enableFilter
                                />
                            )}
                        </div>
                        <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
                            <RHFInput
                                disabled={disabled}
                                highlight={highlightFields.includes('email')}
                                name="email"
                                label={t('email')}
                            />
                            <RHFInput
                                disabled={disabled}
                                highlight={highlightFields.includes('phone_number')}
                                name="phone_number"
                                label={t('phone-number')}
                            />
                        </div>
                    </div>
                )}

                {toolTab === TABS.DEFAULT_VALUES && (
                    <div className="grid gap-6">
                        <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
                            <RHFSelectProduct name="incomes_product_id" label={t('collection-account')} />
                            <RHFSelectProduct name="expenses_product_id" label={t('payment-account')} />
                        </div>

                        <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
                            <RHFSelectMultipleTaxes name="taxes_incomes_ids" label={t('taxes-incomes')} />
                            <RHFSelectMultipleTaxes name="taxes_expenses_ids" label={t('taxes-expenses')} />
                        </div>

                        <Divider className="my-4" />

                        <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
                            <RHFSelect options={languagesOptions} name="language" label={t('language')} />
                            <RHFSelect options={currenciesOptions} name="currency" label={t('currency')} />
                        </div>

                        <div className="grid md:grid-cols-2 gap-x-5 gap-y-4">
                            <RHFSelect options={dueDaysOptions} name="due_days" label={t('due-days')} />
                            <RHFSelect options={paymentDaysOptions} name="payment_day" label={t('payment-day')} />
                        </div>

                        {showVerifactuConfig && (
                            <>
                                <Divider className="my-4" />

                                <ContactEditorDrawerDefaultValuesForm
                                    enabled={defaultValuesEnabled}
                                    setEnabled={setDefaultValuesEnabled}
                                    values={taxAuthorityValues}
                                    onChangeValue={(v) => setTaxAuthorityValues((prev) => ({ ...prev, ...v }))}
                                    isLoading={isLoadingTaxAuthority}
                                    taxAuthority={taxAuthority}
                                />
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button
                        variant="primary"
                        isBusy={processing}
                        type="submit"
                        className="grow"
                        testId="contact-editor.save"
                        onClick={() => setToolTab(TABS.GENERAL)}
                    >
                        {submitButton}
                    </Button>
                    <Button className="grow" onClick={onCancel} testId="contact-editor.cancel">
                        {cancelButton}
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
}

ContactEditorDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    contact: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    initialTab: PropTypes.oneOf(Object.values(TABS)),
    hasAutomaticKeyword: PropTypes.bool,
    highlightFields: PropTypes.arrayOf(PropTypes.string),
    submitButtonText: PropTypes.string,
    cancelButtonText: PropTypes.string,
};

ContactEditorDrawer.drawerStackProps = {
    size: 'lg',
};
